/**
 * @fileoverview Animation effects for game interactions.
 * Creates particle effects like star bursts and confetti using Konva shapes.
 */

import Konva from "konva";

/**
 * Animation effects utility class.
 * Provides static methods for creating particle effects and animations.
 */
export class AnimationEffects {
    /**
     * Create a star burst animation at the specified position.
     * Multiple star particles expand outward and fade away.
     * 
     * @param {Konva.Layer} layer - Konva layer to add animations to
     * @param {number} x - X coordinate for center of burst
     * @param {number} y - Y coordinate for center of burst
     * @param {string} color - Color of the stars
     */
    static createStarBurst(layer: Konva.Layer, x: number, y: number, color: string): void {
    const numStars = 20;
    const burstRadius = 200;
    const starSize = 20;
    
    for (let i = 0; i < numStars; i++) {
        const angle = (Math.PI * 2 * i) / numStars;
        const endX = x + Math.cos(angle) * burstRadius;
        const endY = y + Math.sin(angle) * burstRadius;
        
        // Create star particle
        const star = new Konva.Star({
            x: x,
            y: y,
            numPoints: 5,
            innerRadius: starSize / 2,
            outerRadius: starSize,
            fill: color,
            opacity: 1,
            rotation: Math.random() * 360
        });
        
        layer.add(star);
        
        // Animate star expanding outward and fading
        star.to({
            x: endX,
            y: endY,
            opacity: 0,
            rotation: star.rotation() + 180,
            duration: 1.5,
            easing: Konva.Easings.EaseOut,
            onFinish: () => {
                star.destroy();
            }
        });
    }
    
    // Add a central flash
    const flash = new Konva.Circle({
        x: x,
        y: y,
        radius: 5,
        fill: 'white',
        opacity: 1
    });
    
    layer.add(flash);
    
    flash.to({
        radius: 30,
        opacity: 0,
        duration: 0.4,
        onFinish: () => {
            flash.destroy();
        }
    });
    
        layer.batchDraw();
    }

    /**
     * Create confetti animation that falls from the top of the screen.
     * Colorful rectangles fall with rotation and gravity.
     * 
     * @param {Konva.Stage} stage - Konva stage to get dimensions
     * @param {Konva.Layer} layer - Konva layer to add confetti to
     */
    static createConfetti(stage: Konva.Stage, layer: Konva.Layer): void {
    const colors = ['#FF6B35', '#4ECDC4', '#FFD93D', '#FF6B9D', '#A8E6CF', '#957DAD'];
    const numPieces = 50;
    const stageWidth = stage.width();
    const stageHeight = stage.height();
    
    for (let i = 0; i < numPieces; i++) {
        const color = colors[Math.floor(Math.random() * colors.length)];
        const x = Math.random() * stageWidth;
        const size = 8 + Math.random() * 8;
        const rotation = Math.random() * 360;
        const delay = Math.random() * 0.3;
        
        // Create confetti piece
        const piece = new Konva.Rect({
            x: x,
            y: -20,
            width: size,
            height: size * 1.5,
            fill: color,
            rotation: rotation,
            opacity: 1
        });
        
        layer.add(piece);
        
        // Animate falling with rotation
        const fallDuration = 2 + Math.random() * 1;
        const endY = stageHeight + 50;
        const drift = (Math.random() - 0.5) * 100;
        
        piece.to({
            y: endY,
            x: x + drift,
            rotation: rotation + 360 + Math.random() * 360,
            opacity: 0.7,
            duration: fallDuration,
            delay: delay,
            easing: Konva.Easings.EaseIn,
            onFinish: () => {
                piece.destroy();
            }
        });
        }
        
        layer.batchDraw();
    }

    /**
     * Create a floating emoji that appears, scales up, and floats away.
     * 
     * @param {Konva.Layer} layer - Konva layer to add emoji to
     * @param {number} x - X coordinate
     * @param {number} y - Y coordinate
     * @param {string} emoji - Emoji character to display
     */
    static createFloatingEmoji(layer: Konva.Layer, x: number, y: number, emoji: string): void {
    const text = new Konva.Text({
        x: x,
        y: y,
        text: emoji,
        fontSize: 0,
        opacity: 0
    });
    
    // Center the text
    text.offsetX(text.width() / 2);
    text.offsetY(text.height() / 2);
    
    layer.add(text);
    
    // Scale up and appear
    text.to({
        fontSize: 60,
        opacity: 1,
        duration: 0.3,
        easing: Konva.Easings.BackEaseOut,
        onFinish: () => {
            // Float upward and fade
            text.to({
                y: y - 100,
                opacity: 0,
                fontSize: 64,
                duration: 1.5,
                easing: Konva.Easings.EaseOut,
                onFinish: () => {
                    text.destroy();
                }
            });
            }
        });
        
        layer.batchDraw();
    }
}
